import { useState } from 'react';
import { useGuild } from '../contexts/GuildContext';
import { useToast } from '../contexts/ToastContext';
import { usePunishments, useCancelPunishment } from '../hooks/usePunishments';
import { getErrorMessage } from '../api/errorHandler';
import DashboardLayout from '../components/DashboardLayout';
import ServerSelector from '../components/ServerSelector';
import PunishmentFilters from '../components/PunishmentFilters';
import CancelPunishmentModal from '../components/CancelPunishmentModal';
import Pagination from '../components/Pagination';
import type { Punishment } from '../types';

export default function HistoryPage() {
  const { selectedGuildId, setSelectedGuildId } = useGuild();
  const { showToast } = useToast();
  const [userId, setUserId] = useState('');
  const [type, setType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [selectedPunishment, setSelectedPunishment] = useState<Punishment | null>(null);

  const limit = 10;

  const { data, isLoading, error } = usePunishments(selectedGuildId, {
    userId: userId || undefined,
    type: type || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    page,
    limit,
  });

  const cancelPunishment = useCancelPunishment(selectedGuildId);

  const handleReset = () => {
    setUserId('');
    setType('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleCancelPunishment = async (punishmentId: string, reason: string) => {
    try {
      await cancelPunishment.mutateAsync({ punishmentId, reason });
      showToast('success', '제재가 성공적으로 취소되었습니다');
      setSelectedPunishment(null);
    } catch (error) {
      showToast('error', getErrorMessage(error));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">제재 기록</h1>
              <p className="text-gray-600">서버의 모든 제재 기록을 조회하고 관리하세요</p>
            </div>
            <div className="mt-4 md:mt-0 md:w-80">
              <ServerSelector
                selectedGuildId={selectedGuildId}
                onSelectGuild={setSelectedGuildId}
              />
            </div>
          </div>

          {!selectedGuildId ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-700">서버를 선택해주세요</p>
            </div>
          ) : (
            <>
              <PunishmentFilters
                userId={userId}
                type={type}
                startDate={startDate}
                endDate={endDate}
                onUserIdChange={setUserId}
                onTypeChange={setType}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onReset={handleReset}
              />

              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700">제재 기록을 불러올 수 없습니다</p>
                </div>
              ) : !data || !data.punishments || data.punishments.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <p className="text-gray-500">제재 기록이 없습니다</p>
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              사용자 ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              제재 유형
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              사유
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              규칙
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              날짜
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              상태
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              작업
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {data.punishments.map((punishment) => (
                            <tr key={punishment._id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {punishment.userId}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${
                                    punishment.punishmentType === 'warn'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : punishment.punishmentType === 'timeout'
                                      ? 'bg-orange-100 text-orange-800'
                                      : punishment.punishmentType === 'kick'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-900 text-white'
                                  }`}
                                >
                                  {punishment.punishmentType}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                {punishment.reason}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {punishment.ruleName || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(punishment.timestamp)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {punishment.cancelled ? (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
                                    취소됨
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                    활성
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {!punishment.cancelled && (
                                  <button
                                    onClick={() => setSelectedPunishment(punishment)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    취소
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {totalPages > 1 && (
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <CancelPunishmentModal
        punishment={selectedPunishment}
        onClose={() => setSelectedPunishment(null)}
        onConfirm={handleCancelPunishment}
        isLoading={cancelPunishment.isPending}
      />
    </DashboardLayout>
  );
}
